export const API_ENDPOINTS = {
  HEALTH: '/health',

  AUTH_LOGIN: '/api/auth/login',
  AUTH_LOGOUT: '/api/auth/logout',
  AUTH_REFRESH: '/api/auth/refresh',
  AUTH_CURRENT_USER: '/api/auth/me',
  AUTH_CHANGE_PASSWORD: '/api/auth/change-password',
  AUTH_FORGOT_PASSWORD: '/api/auth/forgot-password',
  AUTH_RESET_PASSWORD: '/api/auth/reset-password',

  USERS: '/api/users',
  USER_BY_ID: (id: string) => `/api/users/${id}`,

  ORGANIZATIONS: '/api/organizations',
  ORGANIZATION_BY_ID: (id: number) => `/api/organizations/${id}`,

  PROPERTIES: '/api/properties',
  PROPERTY_BY_ID: (id: number) => `/api/properties/${id}`,

  DEVICES: '/api/devices',
  DEVICE_BY_ID: (id: number) => `/api/devices/${id}`,
  DEVICE_REGISTER: '/api/devices/register',
  DEVICE_HEARTBEAT: '/api/devices/heartbeat',

  TEAMS: '/api/teams',
  TEAM_BY_ID: (id: number) => `/api/teams/${id}`,

  TABLES: '/api/tables',
  TABLE_BY_ID: (id: number) => `/api/tables/${id}`,


  MENU: '/restaurant/menu',
  MENU_ITEM_BY_ID: (id: number) => `/restaurant/menu/${id}`,
  MENU_CATEGORIES: '/restaurant/menu/categories',

  ORDERS: '/restaurant/orders',
  ORDER_BY_ID: (id: number) => `/restaurant/orders/${id}`,

  PRINTERS: '/restaurant/printers',
  PRINTER_BY_ID: (id: number) => `/restaurant/printers/${id}`,

  CALLS: '/restaurant/calls',
  CALL_MESSAGES: (id: number) => `/restaurant/calls/${id}/messages`,
  // Freshly signed per request and dead in an hour — call it on play, never
  // when the list renders.
  CALL_AUDIO_URL: (id: number) => `/restaurant/calls/${id}/audio-url`,
  // Pushes `call` and `order` events. Same wire contract as TRANSCRIPTS_STREAM;
  // the token is appended by the caller because EventSource cannot set headers.
  RESTAURANT_STREAM: (propertyId: number) => `/restaurant/stream?property_id=${propertyId}`,

  TASKS: '/api/tasks',
  TASK_BY_ID: (id: number) => `/api/tasks/${id}`,
  TASK_STATUS: (id: number) => `/api/tasks/${id}/status`,

  TRANSCRIPTS: '/api/conversations/transcripts',
  TRANSCRIPTS_FILTERED: (params: string) => `/api/conversations/transcripts?${params}`,
  TRANSCRIPTS_BY_DAY: (propertyId: number, day: string) =>
    `/api/conversations/transcripts?property_id=${propertyId}&day=${day}`,
  TRANSCRIPTS_STREAM: (propertyId: number, token?: string) =>
    token
      ? `/api/conversations/transcripts/stream?property_id=${propertyId}&access_token=${encodeURIComponent(token)}`
      : `/api/conversations/transcripts/stream?property_id=${propertyId}`,
  TASK_NOTIFICATIONS_STREAM: (token: string) =>
    `/api/conversations/task-notifications/stream?access_token=${encodeURIComponent(token)}`,
  CONVERSATIONS_SUMMARY: (params: string) => `/api/conversations/summary?${params}`,

  // Provision (multipart — org + admin + logo in one call)
  ORGS_PROVISION: '/api/organizations/provision',

  // Platform overview
  PLATFORM_OVERVIEW: '/api/platform/overview',

  // Platform org management (scoped under /api/platform/organizations)
  PLATFORM_ORGS: '/api/platform/organizations',
  PLATFORM_ORG_BY_ID: (orgId: number) => `/api/platform/organizations/${orgId}`,
  PLATFORM_ORG_LOGO: (orgId: number) => `/api/platform/organizations/${orgId}/logo`,
  PLATFORM_ORG_SUSPEND: (orgId: number) => `/api/platform/organizations/${orgId}/suspend`,
  PLATFORM_ORG_UNSUSPEND: (orgId: number) => `/api/platform/organizations/${orgId}/unsuspend`,
  PLATFORM_ORGS_BULK_UNSUSPEND: '/api/platform/organizations/bulk-unsuspend',

  // Platform org → users
  PLATFORM_ORG_USERS: (orgId: number) => `/api/platform/organizations/${orgId}/users`,
  PLATFORM_ORG_USER: (orgId: number, userId: number) =>
    `/api/platform/organizations/${orgId}/users/${userId}`,
  PLATFORM_ORG_USER_STATUS: (orgId: number, userId: number) =>
    `/api/platform/organizations/${orgId}/users/${userId}/status`,

  // Platform org → properties
  PLATFORM_ORG_PROPERTIES: (orgId: number) => `/api/platform/organizations/${orgId}/properties`,
  PLATFORM_ORG_PROPERTY: (orgId: number, propId: number) =>
    `/api/platform/organizations/${orgId}/properties/${propId}`,

  // Platform org → devices
  PLATFORM_ORG_DEVICES: (orgId: number, propId?: number) =>
    propId
      ? `/api/platform/organizations/${orgId}/devices?property_id=${propId}`
      : `/api/platform/organizations/${orgId}/devices`,
  PLATFORM_ORG_DEVICE: (orgId: number, deviceId: number) =>
    `/api/platform/organizations/${orgId}/devices/${deviceId}`,

  // Platform analytics endpoints
  PLATFORM_ANALYTICS_USAGE: '/api/platform/analytics/usage',
  PLATFORM_ANALYTICS_ORG_USAGE: (orgId: number) => `/api/platform/analytics/organizations/${orgId}/usage`,
  PLATFORM_ANALYTICS_TEAMS: '/api/platform/analytics/teams',
  PLATFORM_ANALYTICS_INCIDENTS: '/api/platform/analytics/incidents',
  PLATFORM_ANALYTICS_DEVICE_HEALTH: '/api/platform/analytics/device-health',
  PLATFORM_ANALYTICS_TASK_PERFORMANCE: '/api/platform/analytics/tasks/performance',
  PLATFORM_ANALYTICS_CONSUMPTION: '/api/platform/analytics/consumption',
  PLATFORM_ANALYTICS_DEVICE_INCIDENTS: '/api/platform/analytics/device-incidents',
  PLATFORM_ANALYTICS_OTA_ROLLOUT: '/api/platform/analytics/ota-rollout',

  // Per-org device health + single device
  PLATFORM_ORG_DEVICE_HEALTH: (orgId: number) => `/api/platform/organizations/${orgId}/device-health`,
  PLATFORM_DEVICE_SINGLE_HEALTH: (orgId: number, deviceId: number) =>
    `/api/platform/organizations/${orgId}/devices/${deviceId}/device-health`,

  // Device incident resolve
  PLATFORM_DEVICE_INCIDENT_RESOLVE: (orgId: number, deviceId: number, incidentId: number) =>
    `/api/platform/organizations/${orgId}/devices/${deviceId}/device-incidents/${incidentId}/resolve`,

  // OTA releases
  PLATFORM_OTA_RELEASES: '/api/platform/ota/releases',
  PLATFORM_OTA_RELEASE_BY_ID: (id: number) => `/api/platform/ota/releases/${id}`,

  // Device commands
  PLATFORM_ORG_DEVICE_COMMANDS: (orgId: number, deviceId: number) =>
    `/api/platform/organizations/${orgId}/devices/${deviceId}/commands`,
  PLATFORM_ORG_DEVICE_COMMAND_CANCEL: (orgId: number, deviceId: number, cmdId: number) =>
    `/api/platform/organizations/${orgId}/devices/${deviceId}/commands/${cmdId}/cancel`,

  // Support tickets
  SUPPORT_TICKETS_PUBLIC: '/api/support/tickets',
  SUPPORT_TICKETS_ME: '/api/support/tickets/me',
  SUPPORT_TICKET_ME_BY_ID: (id: number) => `/api/support/tickets/me/${id}`,
  PLATFORM_SUPPORT_TICKETS: '/api/platform/support/tickets',
  PLATFORM_SUPPORT_TICKET_STATUS: (id: number) => `/api/platform/support/tickets/${id}/status`,
  PLATFORM_SUPPORT_TICKET_REPLY: (id: number) => `/api/platform/support/tickets/${id}/reply`,

  // Platform password reset endpoints
  PLATFORM_RESET_ORG_USER: (orgId: number, userId: number) =>
    `/api/platform/organizations/${orgId}/users/${userId}/reset-password`,
  PLATFORM_RESET_PROPERTY_ADMIN: (orgId: number, propId: number) =>
    `/api/platform/organizations/${orgId}/properties/${propId}/admin/reset-password`,
  PLATFORM_RESET_PLATFORM_ADMIN: (adminId: number) =>
    `/api/platform/admins/${adminId}/reset-password`,

  // Platform monitoring endpoints
  PLATFORM_MONITORING_OVERVIEW: '/api/platform/monitoring/overview',
  PLATFORM_MONITORING_SERVICES: '/api/platform/monitoring/services',
  PLATFORM_MONITORING_PERFORMANCE: '/api/platform/monitoring/api-performance',
  PLATFORM_MONITORING_ERRORS: '/api/platform/monitoring/errors',
  PLATFORM_MONITORING_ERROR_RESOLVE: (id: number) => `/api/platform/monitoring/errors/${id}/resolve`,
  PLATFORM_MONITORING_SYSTEM: '/api/platform/monitoring/system',

  // Platform admin endpoints
  PLATFORM_ADMINS: '/api/platform/admins',
  PLATFORM_ADMIN_BY_ID: (id: number) => `/api/platform/admins/${id}`,
  PLATFORM_ADMIN_STATUS: (id: number) => `/api/platform/admins/${id}/status`,
  PLATFORM_AUDIT_LOGS: '/api/platform/audit-logs',

  SHIFTS: '/api/shifts',
  SHIFT_START: '/api/shifts/start',
  SHIFT_CURRENT: '/api/shifts/current',
  SHIFT_BY_ID: (id: number) => `/api/shifts/${id}`,
  SHIFT_NOTES: (id: number) => `/api/shifts/${id}/notes`,
  SHIFT_HANDOFF: (id: number) => `/api/shifts/${id}/handoff`,
  SHIFT_HANDOFFS: (id: number) => `/api/shifts/${id}/handoffs`,
  SHIFT_HANDOFFS_PENDING: '/api/shifts/handoffs/pending',
  SHIFT_HANDOFF_ACK: (handoffId: number) => `/api/shifts/handoffs/${handoffId}/acknowledge`,
  SHIFT_HANDOFF_DECLINE: (handoffId: number) => `/api/shifts/handoffs/${handoffId}/decline`,
} as const
